/**
 * Quais aulas o seletor "Aula do dia" do Diário deve oferecer.
 *
 * Duas coisas moram aqui.
 *
 * 1) O recorte era só pela disciplina. Numa escola com duas professoras de
 *    Português, o seletor oferecia a aula da colega junto com a dela — e o
 *    horário exibido era o de uma aula em que a professora está em OUTRA
 *    turma. Foi o que a professora Diane relatou: "os horários que aparecem
 *    para preencher o diário nesse dia são as aulas que estou em outra
 *    turma". Por isso o professor vê as aulas DELE.
 *
 * 2) Quem substitui um colega que faltou não achava o horário coberto na
 *    lista: caía em "sem aula na grade" e a aula era gravada SEM período. Os
 *    dois lados ficavam errados — o horário de quem faltou seguia pendente
 *    para sempre no painel, como se ninguém tivesse coberto, e a aula dada
 *    aparecia solta como "extra", sem crédito do horário.
 *
 *    O Acompanhamento já sabia premiar a substituição (aula lançada NO
 *    período do horário, com outra disciplina, conta como horário cumprido) e
 *    o backend já aceitava gravar assim. Faltava a tela oferecer.
 *
 * Então a lista sai em duas partes: as aulas do professor e, depois, os
 * outros horários daquele dia — para ele poder dizer "cobri a 1ª aula, que
 * era de Geografia". Cada item diz a qual grupo pertence, e a tela rotula.
 *
 * A segunda parte só entra quando quem chama pede (`incluirSubstituicoes`).
 * Na tela, quem pede é o tipo "Extra / Substituição": no dia a dia o
 * professor lança a aula dele, e a lista dos horários dos colegas no meio do
 * caminho é ruído. Ele diz primeiro que está cobrindo alguém; aí a lista
 * cresce.
 */

/** Só os horários que vêm da grade podem ser cobertos. */
const ehHorarioDaGrade = (aula) => Boolean(aula && aula.id_schedule);

/**
 * @param {Array}  aulasDoDia   resposta de /class-schedule/for_date/ (lessons)
 * @param {Object} opcoes
 * @param {*}      opcoes.idDisciplina  disciplina escolhida no topo do Diário
 * @param {*}      opcoes.idTeacher     cadastro do professor logado, ou null
 *                                      para quem não é professor (coordenação
 *                                      vê o dia inteiro — é o papel dela)
 * @param {boolean} opcoes.incluirSubstituicoes  também oferecer os horários
 *                                      dos colegas que seguem descobertos
 * @returns {Array} as aulas, cada uma com `substituindo: boolean`
 */
export function aulasParaOSeletor(
  aulasDoDia,
  { idDisciplina, idTeacher, incluirSubstituicoes = false } = {}
) {
  const doDia = aulasDoDia || [];
  const daDisciplina = doDia.filter(
    (aula) => String(aula.id_subject) === String(idDisciplina)
  );

  const marcar = (aulas, substituindo) =>
    aulas.map((aula) => ({ ...aula, substituindo }));

  if (!idTeacher) return marcar(daDisciplina, false);

  const minhas = daDisciplina.filter(
    (aula) => String(aula.id_teacher) === String(idTeacher)
  );

  // A volta atrás importa: `id_teacher` do horário é opcional — quando está
  // vazio, quem responde pela aula é o vínculo da disciplina. Filtrar sem essa
  // saída deixaria o professor com um seletor vazio numa grade que existe, que
  // é justamente o beco em que a chamada ficava presa antes.
  const proprias = minhas.length ? minhas : daDisciplina;
  const periodosProprios = new Set(proprias.map((a) => String(a.period)));

  // Os outros horários do dia — o que ele pode ter coberto. Ficam de fora:
  //
  //  - o que já é dele: não se substitui a si mesmo;
  //  - o que não é horário da grade: aula avulsa que alguém lançou não é um
  //    horário a cobrir;
  //  - o horário que JÁ tem aula lançada: o colega não faltou, não há o que
  //    cobrir. Oferecer isso convidava a gravar por cima do lançamento dele —
  //    e o painel de acompanhamento lê uma aula por período, então a segunda
  //    seria descartada sem ninguém ser avisado.
  const paraSubstituir = doDia.filter(
    (aula) => ehHorarioDaGrade(aula)
      && !aula.registered
      && !periodosProprios.has(String(aula.period))
  );

  const porPeriodo = (a, b) => Number(a.period) - Number(b.period);
  const lista = marcar([...proprias].sort(porPeriodo), false);
  if (!incluirSubstituicoes) return lista;
  return [...lista, ...marcar([...paraSubstituir].sort(porPeriodo), true)];
}

/** Identifica a aula do dia: pelo registro no diário, ou pelo horário da grade. */
export const chaveDaAulaDoDia = (aula) => (aula.id_diary_entry
  ? `aula-${aula.id_diary_entry}`
  : `slot-${aula.period}-${aula.id_subject}`);

/**
 * A mesma aula em duas leituras de /class-schedule/for_date/. Duas aulas já
 * no diário se comparam pelo registro; senão, pelo período e disciplina — é
 * o caso do horário da grade que ganha o registro ao salvar a chamada.
 *
 * Antes período e disciplina iguais bastavam sempre. Duas aulas avulsas da
 * mesma disciplina no dia (sem grade, ambas com período vazio) eram "a mesma
 * aula": depois de salvar a chamada de uma, a tela pulava para a outra, ainda
 * sem chamada — e a frequência "sumia".
 */
export const mesmaAula = (a, b) => {
  if (!a || !b) return false;
  if (a.id_diary_entry && b.id_diary_entry) return a.id_diary_entry === b.id_diary_entry;
  return a.period != null
    && String(a.period) === String(b.period)
    && String(a.id_subject) === String(b.id_subject);
};

/**
 * As outras aulas do professor que podem receber o mesmo registro — as aulas
 * geminadas (1ª e 2ª aula de Matemática, uma atrás da outra).
 *
 * Antes cada uma era registrada à parte, com o mesmo conteúdo digitado de
 * novo, e a chamada lançada duas vezes. Ficam de fora a aula já escolhida, as
 * que já estão no diário e os horários de colegas (substituição é uma aula
 * só).
 *
 * @param {Array} aulas  saída de `aulasParaOSeletor`
 * @param {*} periodoEscolhido  a "Aula do dia" do formulário
 */
export function outrasAulasDoBloco(aulas, periodoEscolhido) {
  if (!periodoEscolhido) return [];
  return (aulas || [])
    .filter((aula) => !aula.substituindo
      && !aula.registered
      && aula.period
      && String(aula.period) !== String(periodoEscolhido))
    .sort((a, b) => Number(a.period) - Number(b.period));
}

/**
 * A aula dada ainda sem o registro do que foi trabalhado.
 *
 * A BNCC é obrigatória em toda aula dada; quem não a tem é a aula que o
 * sistema criou sozinho — a professora lançou só a chamada (ou a planilha
 * de frequência foi importada) e ficou um texto automático no lugar do
 * conteúdo. Aula que não aconteceu não tem conteúdo a cobrar.
 */
export function aulaSemConteudo(entry) {
  if (!entry || entry.lesson_type === 'nao_realizada') return false;
  return !String(entry.bncc_skills || '').trim();
}

/**
 * O texto que o sistema põe no conteúdo da aula criada automaticamente.
 * Ao completar o registro, o campo abre vazio em vez de mostrar isto como
 * se fosse o conteúdo da professora.
 */
export function conteudoAutomatico(texto) {
  return /\((criada ao lançar a frequência(, sem grade horária)?|importado via planilha)\)\s*$/
    .test(String(texto || ''));
}

/**
 * A aula registrada já não pede nada ao professor — o verde do Registro de
 * Aulas. Mesma regra do calendário (`_aula_resolvida` no backend).
 *
 * Antes só o "✓ Marcar como concluída" tirava a aula do laranja. Ninguém
 * clicava: a professora registrava a aula, lançava a chamada e o dia seguia
 * laranja — "já tá registrado, mas continua laranja". Achando que faltava
 * registrar, tentava gravar de novo e caía no erro de aula repetida.
 *
 * Resolvida é a aula que não aconteceu (não há chamada a lançar) ou a que tem
 * conteúdo E chamada lançada (ou foi marcada como concluída à mão). Sem o
 * conteúdo, a aula criada só pela chamada ficava verde com nada registrado.
 */
export function aulaResolvida(entry) {
  if (!entry) return false;
  if (entry.lesson_type === 'nao_realizada') return true;
  if (aulaSemConteudo(entry)) return false;
  return Boolean(entry.is_completed || entry.attendance_filled);
}

/** Rótulo do horário no seletor: "3ª aula • 08:25–09:15". */
export function rotuloDaAula(aula) {
  if (!aula) return '';
  const base = `${aula.period}ª aula${aula.time_label ? ` • ${aula.time_label}` : ''}`;
  if (!aula.substituindo) {
    return base + (aula.registered ? ' (já registrada)' : '');
  }
  // Na substituição, de quem é o horário é a informação que importa: é o que
  // deixa claro que ele está cobrindo o colega, e não lançando a própria aula.
  const dono = [aula.subject_name, aula.teacher_name].filter(Boolean).join(' · ');
  return `Substituindo · ${base}${dono ? ` — ${dono}` : ''}`;
}
